import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Accordion,
  Row,
  Col,
  Spinner,
  Alert,
  Button,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import useFetchById from 'hooks/useFetchById';

import SubtleBadge from 'components/common/SubtleBadge';

import { manifestApi } from 'api';
import paths from 'routes/paths';
import { generateManifestPdf } from 'utils/pdfGenerators';
import { formatStatus } from 'utils/valueFormatters';
import { getManifestBadgeColor } from 'constants/manifest';
import { TIME_OPTION } from 'constants/time';

const ManifestDetailSummary = ({
  manifestId,
  showActionBtn = false,
  listenToUpdates = false,
}) => {
  const navigate = useNavigate();
  const [activeKeys, setActiveKeys] = useState(['0', '1']);

  const {
    data: manifestData,
    loading,
    error,
    refetch,
  } = useFetchById(manifestApi, manifestId);

  useEffect(() => {
    if (listenToUpdates) {
      const handleUpdate = () => {
        refetch();
      };

      window.addEventListener('manifest-updated', handleUpdate);

      return () => window.removeEventListener('manifest-updated', handleUpdate);
    }
  }, [listenToUpdates, refetch]);

  const handleSelect = key => {
    setActiveKeys(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const handleCloseManifest = async () => {
    try {
      const response = await manifestApi.closeManifest(manifestId);

      toast.success('Successfully closed manifest');
      navigate(paths.manifests);
    } catch (error) {
      toast.error('Failed to close manifest');
    }
  };

  const handleDiscardManifest = async () => {
    try {
      const response = await manifestApi.discardManifest(manifestId);

      toast.success('Successfully discarded manifest');
      navigate(paths.manifests);
    } catch (error) {
      toast.error('Failed to discard manifest');
    }
  };

  const handlePrintManifest = async (
    manifestId,
    manifestCode,
    shippingProvider,
    channel,
  ) => {
    try {
      const params = new URLSearchParams();
      params.append('paging', 'false');

      const response = await manifestApi.getShipmentsOfManifest(
        manifestId,
        params,
      );

      generateManifestPdf(response, manifestCode, shippingProvider, channel);
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  if (loading) {
    return (
      <Card className="d-flex justify-content-center align-items-center p-4">
        <Spinner animation="border" variant="primary" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {showActionBtn && (
        <div className="d-flex justify-content-end gap-2 mb-3">
          {manifestData?.shipment_quantity > 0 ? (
            <>
              <Button
                variant="falcon-default"
                size="sm"
                onClick={handleCloseManifest}
              >
                Close Manifest
              </Button>

              <Button
                variant="falcon-default"
                size="sm"
                onClick={() =>
                  handlePrintManifest(
                    manifestId,
                    manifestData?.manifest_code,
                    manifestData?.shipping_provider.shipping_provider_type,
                    manifestData?.channel.channel_type,
                  )
                }
              >
                <FontAwesomeIcon icon="file-alt" />
              </Button>
            </>
          ) : (
            <Button
              variant="falcon-default"
              size="sm"
              onClick={handleDiscardManifest}
            >
              Discard Manifest
            </Button>
          )}
        </div>
      )}
      {/* Close 버튼 */}
      <Accordion activeKey={activeKeys} onSelect={handleSelect}>
        <Card>
          {/* General Information */}
          <Accordion.Item eventKey="0">
            <Accordion.Header className="d-flex justify-content-between">
              <span className="fw-semibold">General Information</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="p-3">
                <Row className="g-2">
                  <Col xs={6} className="text-muted fs-10">
                    Code
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {manifestData?.manifest_code}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Status
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    <SubtleBadge
                      bg={getManifestBadgeColor(manifestData?.manifest_status)}
                    >
                      {formatStatus(manifestData?.manifest_status)}
                    </SubtleBadge>
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Shipping Provider
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {manifestData?.shipping_provider?.shipping_provider_type ||
                      '-'}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Shipping Courier
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {manifestData?.shipping_courier?.name || '-'}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Date
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {new Date(manifestData?.created).toLocaleString(
                      'en-IN',
                      TIME_OPTION,
                    )}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Shipping Method
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {manifestData?.shipping_method}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Channel
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {manifestData?.channel?.channel_type}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    User
                  </Col>
                  <Col xs={12} className="fw-medium fs-10 mt-0">
                    {manifestData?.created_by?.email || '-'}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Comments
                  </Col>
                  <Col xs={12} className="fw-medium fs-10 mt-0">
                    {manifestData?.comments || '-'}
                  </Col>
                </Row>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Summary */}
          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <span className="fw-semibold">Summary</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="p-3">
                <Row className="g-2">
                  <Col xs={6} className="text-muted fs-10">
                    Total Shipments
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {manifestData?.shipment_quantity}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Provider Name
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {manifestData?.shipping_provider?.shipping_provider_type ||
                      '-'}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    No. of Products
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {manifestData?.product_quantity}
                  </Col>
                </Row>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Card>
      </Accordion>
    </>
  );
};

export default ManifestDetailSummary;
